import type { MouseEventHandler } from 'react'
import { useCallback } from 'react'
import { useEffect } from 'react'
import { useMutation } from '@connectrpc/connect-query'
import { Trans } from '@lingui/react/macro'

import { federate as federateDescriptor } from '@jotta/grpc-connect-openapi/esm/no/jotta/openapi/federation/v1/federation.v1-FederationService_connectquery'
import type { FederationIntent as FederationIntentProto } from '@jotta/grpc-connect-openapi/esm/no/jotta/openapi/federation_pb'
import { FederationType } from '@jotta/grpc-connect-openapi/esm/no/jotta/openapi/federation_pb'
import type { BrandThemeIcon } from '@jotta/types/Brand'
import type { PartialMessage } from '@jotta/types/compat'
import { Button } from '@jotta/ui/Button'
import SvgApple from '@jotta/ui/SvgApple'
import SvgFacebook from '@jotta/ui/SvgFacebook'
import SvgGoogle from '@jotta/ui/SvgGoogle'
import { createNonce, sha256Hex } from '@jotta/utils/crypto'
import { getEnumKey } from '@jotta/utils/enum'
import { capitalize } from '@jotta/utils/text'

import { persistFederationTokenNonce } from './useFederationStore'

export type FederationIntent = PartialMessage<FederationIntentProto>['intent']

function FederateWith({
  intent,
  type,
  icon,
}: {
  intent: FederationIntent
  type: FederationType
  icon: BrandThemeIcon
}) {
  const { data, isPending, mutate: federate } = useMutation(federateDescriptor)
  const redirectUrl = data?.redirectUrl

  useEffect(() => {
    if (redirectUrl) {
      window.location.assign(redirectUrl)
    }
  }, [redirectUrl])

  const federateWith = useCallback<MouseEventHandler>(
    async event => {
      event.preventDefault()
      const nonce = createNonce()
      const hashedNonce = await sha256Hex(nonce)
      federate({
        type,
        intent: {
          returnUrl: location.href,
          intent,
          hashedNonce,
        },
        redirectDomain: location.hostname,
      })
      persistFederationTokenNonce(hashedNonce, nonce)
    },
    [intent, type],
  )
  const federationProviderName = capitalize(
    getEnumKey(FederationType, type).toLowerCase(),
  )
  return (
    <Button
      loading={Boolean(isPending || redirectUrl)}
      className="btn btn-outline"
      icon={icon}
      onClick={federateWith}
    >
      <Trans>Continue with {federationProviderName}</Trans>
    </Button>
  )
}

export function Federation({ intent }: { intent: FederationIntent }) {
  return (
    <div className="flex flex-col gap-4">
      <FederateWith
        intent={intent}
        icon={SvgApple}
        type={FederationType.APPLE}
      />
      <FederateWith
        intent={intent}
        icon={SvgGoogle}
        type={FederationType.GOOGLE}
      />
      <FederateWith
        intent={intent}
        icon={SvgFacebook}
        type={FederationType.FACEBOOK}
      />
    </div>
  )
}
